import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function ArticlesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/articles?limit=100");
        if (mounted) setItems(data || []);
      } catch (err) {
        if (mounted)
          setError(
            err?.response?.data?.detail || "Impossibile caricare gli articoli"
          );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen" data-testid="articles-list-page">
      <Navbar />

      {/* DARK HERO */}
      <section className="relative bg-carbon text-warm pt-32 lg:pt-40 pb-28 lg:pb-36 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-[0.2] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="overline mb-5">Approfondimenti</div>
            <h1 className="font-display text-5xl lg:text-7xl font-medium leading-[1.02] tracking-tight">
              Note, commenti
              <span className="block text-[#C5A880] italic font-normal">
                & analisi legali.
              </span>
            </h1>
            <p className="mt-8 text-base lg:text-lg text-[#FAF9F6]/70 leading-relaxed">
              Elaborati e commenti dello Studio su sentenze rilevanti,
              evoluzioni normative ed operazioni di attualità. Un punto di
              vista tecnico e orientato al business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* LIGHT CONTENT */}
      <section className="relative bg-[#EAECE7] text-[#0A0A0A] pt-16 lg:pt-24 pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading && (
            <div className="text-sm text-[#0A0A0A]/50">Caricamento…</div>
          )}
          {!loading && error && (
            <div className="border border-[#0A0A0A]/10 bg-white p-8 text-[#0A0A0A]/70">
              {error}
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div
              className="border border-[#0A0A0A]/10 bg-white p-10 lg:p-16 flex flex-col items-center text-center"
              data-testid="articles-empty"
            >
              <BookOpen size={28} className="text-[#1B3B36] mb-4" />
              <h3 className="font-display text-2xl">
                Il primo approfondimento sta arrivando.
              </h3>
              <p className="mt-3 text-sm text-[#0A0A0A]/60 max-w-md">
                Torna presto: qui pubblicheremo commenti a sentenze, analisi
                normative e note operative.
              </p>
            </div>
          )}

          {items.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {items.map((a, i) => (
                <motion.article
                  key={a.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                  className="bg-[#F0F1EC] border border-[#0A0A0A]/12 group hover:border-[#1B3B36]/50 hover:bg-[#F5F6F1] transition-colors"
                  data-testid={`article-card-${a.slug}`}
                >
                  <Link to={`/approfondimenti/${a.slug}`} className="block">
                    {a.cover_image ? (
                      <div className="relative h-52 overflow-hidden bg-[#0A0A0A] border-b border-[#0A0A0A]/10">
                        <img
                          src={a.cover_image}
                          alt={a.title}
                          className="w-full h-full object-cover transition-transform duration-[1000ms] ease-out group-hover:scale-105"
                        />
                      </div>
                    ) : (
                      <div className="relative h-52 flex items-center justify-center bg-[#DDDEDA] border-b border-[#0A0A0A]/10">
                        <div className="text-6xl font-display text-[#1B3B36]/40">
                          §
                        </div>
                      </div>
                    )}
                    <div className="p-6 lg:p-7">
                      <div className="flex items-center gap-3 mb-4 text-[10px] tracking-[0.22em] uppercase font-semibold">
                        <span className="text-[#1B3B36]">
                          {a.tag || "Nota"}
                        </span>
                        <span className="text-[#0A0A0A]/25">·</span>
                        <span className="text-[#0A0A0A]/50">
                          {formatDate(a.published_at)}
                        </span>
                      </div>
                      <h2 className="font-display text-xl lg:text-2xl font-medium leading-tight tracking-tight group-hover:text-[#1B3B36] transition-colors">
                        {a.title}
                      </h2>
                      {a.excerpt && (
                        <p className="mt-3 text-sm text-[#0A0A0A]/65 leading-relaxed line-clamp-3">
                          {a.excerpt}
                        </p>
                      )}
                      <div className="mt-6 flex items-center justify-between text-[11px] text-[#0A0A0A]/50">
                        <span>{a.reading_time} min di lettura</span>
                        <ArrowUpRight
                          size={14}
                          className="text-[#1B3B36] group-hover:translate-x-0.5 transition-transform"
                        />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}

          <div className="mt-20">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs text-[#0A0A0A]/55 hover:text-[#1B3B36]"
              data-testid="articles-back-home"
            >
              <ArrowLeft size={14} /> Torna alla home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
